import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInbox, faPaperPlane, faArrowLeft, faCircle,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  GET_MY_CONVERSATIONS,
  GET_CONVERSATION_MESSAGES,
} from '../graphql/queries';
import {
  SEND_MESSAGE,
  MARK_THREAD_READ,
} from '../graphql/mutations';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

const Messages: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();

  const [activeId, setActiveId] = useState<string | null>(searchParams.get('c'));
  const [draft, setDraft] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: convData, loading: convLoading, refetch: refetchConvs } = useQuery(
    GET_MY_CONVERSATIONS,
    { skip: !isAuthenticated, fetchPolicy: 'cache-and-network', pollInterval: 15000 }
  );

  const { data: msgData, loading: msgLoading, refetch: refetchMsgs } = useQuery(
    GET_CONVERSATION_MESSAGES,
    {
      variables: { conversationId: activeId, limit: 100 },
      skip: !activeId || !isAuthenticated,
      fetchPolicy: 'cache-and-network',
      pollInterval: 5000,
    }
  );

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE, {
    onCompleted: (d) => {
      if (d.sendMessage.success) {
        setDraft('');
        refetchMsgs();
        refetchConvs();
      }
    },
  });

  const [markRead] = useMutation(MARK_THREAD_READ);

  // Sync active conversation to URL
  useEffect(() => {
    if (activeId) {
      setSearchParams({ c: activeId }, { replace: true });
      markRead({ variables: { conversationId: activeId } });
    }
  }, [activeId, markRead, setSearchParams]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgData]);

  const conversations: any[] = convData?.myConversations || [];
  const messages: any[] = msgData?.conversationMessages || [];
  const activeConv = conversations.find((c: any) => c.id === activeId);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || !activeId) return;
    sendMessage({ variables: { conversationId: activeId, body: draft.trim() } });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e as any);
    }
  };

  if (!isAuthenticated) {
    return (
      <Layout showSidebar={false}>
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faInbox} className="text-muted fs-1 mb-3" />
          <h4 className="text-muted">Log in to view your messages</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/login')}>
            Log In
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showSidebar={false}>
      <div className="container-fluid px-0" style={{ height: 'calc(100vh - 80px)', overflow: 'hidden' }}>
        <div className="row g-0 h-100">
          {/* Conversation list */}
          <div
            className={`col-12 col-md-4 col-lg-3 border-end bg-white d-flex flex-column ${
              activeId ? 'd-none d-md-flex' : 'd-flex'
            }`}
            style={{ maxHeight: '100%', overflowY: 'auto', background: 'var(--pw-surface)' }}
          >
            <div className="px-3 py-3 border-bottom" style={{ background: '#fff4ea' }}>
              <h5 className="mb-0 fw-bold">Messages</h5>
            </div>

            {convLoading && conversations.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-5 px-3">
                <FontAwesomeIcon icon={faInbox} className="text-muted fs-2 mb-2" />
                <p className="text-muted small">No conversations yet. Contact a seller to start one.</p>
              </div>
            ) : (
              conversations.map((conv: any) => {
                const other = conv.otherParticipant;
                const last = conv.lastMessage;
                const isActive = conv.id === activeId;
                return (
                  <button
                    key={conv.id}
                    onClick={() => setActiveId(conv.id)}
                    className={`list-group-item list-group-item-action d-flex align-items-start gap-2 py-3 px-3 border-0 border-bottom ${
                      isActive ? 'bg-primary bg-opacity-10' : 'bg-white'
                    }`}
                    style={{ textAlign: 'left', borderBottomColor: 'var(--pw-border)' }}
                  >
                    {/* Avatar */}
                    <div
                      className="rounded-circle bg-secondary d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold"
                      style={{ width: 42, height: 42, fontSize: 16, overflow: 'hidden' }}
                    >
                      {other?.profilePicture
                        ? <img src={other.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (other?.displayName || other?.username || '?').charAt(0).toUpperCase()
                      }
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex justify-content-between align-items-baseline">
                        <span className={`small ${conv.unreadCount > 0 ? 'fw-bold' : 'fw-semibold'} text-truncate`}>
                          {other?.displayName || other?.username}
                        </span>
                        {last && (
                          <span className="text-muted ms-2 flex-shrink-0" style={{ fontSize: 11 }}>
                            {timeAgo(last.createdAt)}
                          </span>
                        )}
                      </div>
                      {conv.ad && (
                        <p className="mb-0 text-primary" style={{ fontSize: 11 }}>
                          {conv.ad.title}
                        </p>
                      )}
                      <div className="d-flex align-items-center gap-1">
                        {last && (
                          <p className="mb-0 text-muted text-truncate" style={{ fontSize: 12, maxWidth: 160 }}>
                            {last.sender?.id === user?.id ? 'You: ' : ''}
                            {last.body}
                          </p>
                        )}
                        {conv.unreadCount > 0 && (
                          <span className="badge bg-primary rounded-pill ms-auto" style={{ fontSize: 10 }}>
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Thread panel */}
          <div className={`col-12 col-md-8 col-lg-9 d-flex flex-column bg-light ${!activeId ? 'd-none d-md-flex' : 'd-flex'}`} style={{ maxHeight: '100%', background: 'var(--pw-bg-soft)' }}>
            {!activeId ? (
              <div className="d-flex align-items-center justify-content-center h-100">
                <div className="text-center">
                  <FontAwesomeIcon icon={faInbox} className="text-muted" style={{ fontSize: 48 }} />
                  <p className="text-muted mt-3">Select a conversation to start messaging</p>
                </div>
              </div>
            ) : (
              <>
                {/* Thread header */}
                <div className="bg-white border-bottom px-4 py-3 d-flex align-items-center gap-3">
                  <button
                    className="btn btn-sm btn-link d-md-none p-0 text-dark"
                    onClick={() => setActiveId(null)}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                  {activeConv && (
                    <>
                      <div
                        className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold"
                        style={{ width: 38, height: 38, fontSize: 15, overflow: 'hidden', flexShrink: 0 }}
                      >
                        {activeConv.otherParticipant?.profilePicture
                          ? <img src={activeConv.otherParticipant.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : (activeConv.otherParticipant?.displayName || activeConv.otherParticipant?.username || '?').charAt(0).toUpperCase()
                        }
                      </div>
                      <div>
                        <p className="mb-0 fw-semibold">
                          {activeConv.otherParticipant?.displayName || activeConv.otherParticipant?.username}
                        </p>
                        {activeConv.ad && (
                          <a
                            href={`/ad/${activeConv.ad.slug}`}
                            className="text-muted small text-decoration-none"
                          >
                            Re: {activeConv.ad.title}
                          </a>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-grow-1 overflow-auto px-4 py-3" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {msgLoading && messages.length === 0 ? (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-5 text-muted small">No messages yet. Say hello!</div>
                  ) : (
                    messages.map((msg: any, idx: number) => {
                      const isMine = msg.sender?.id === String(user?.id);
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(idx * 0.015, 0.3) }}
                          className={`d-flex ${isMine ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                          <div
                            className={`px-3 py-2 rounded-3 shadow-sm ${
                              isMine ? 'bg-primary text-white' : 'bg-white text-dark'
                            }`}
                            style={{ maxWidth: '70%', wordBreak: 'break-word' }}
                          >
                            <p className="mb-0 small">{msg.body}</p>
                            <p className={`mb-0 mt-1 ${isMine ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: 10 }}>
                              {timeAgo(msg.createdAt)}
                              {isMine && (
                                <FontAwesomeIcon
                                  icon={faCircle}
                                  className={`ms-1 ${msg.isRead ? 'text-info' : ''}`}
                                  style={{ fontSize: 6, opacity: msg.isRead ? 1 : 0.4 }}
                                />
                              )}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Compose */}
                <div className="bg-white border-top px-4 py-3">
                  <form onSubmit={handleSend} className="d-flex gap-2 align-items-end">
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      placeholder="Type a message… (Enter to send)"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={{ resize: 'none' }}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={sending || !draft.trim()}
                      style={{ flexShrink: 0 }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Messages;
